// packages/chatbot/src/components/MessageList.tsx
import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import type { UIMessage } from '../types';
import type { ToolUiConfig } from '../types';
import { MessageBubble } from './MessageBubble';
import { ToolCallBubble } from './ToolCallBubble';
import { ToolResultBubble } from './ToolResultBubble';
import { SystemMessageBubble } from './SystemMessageBubble';

type ResolvedToolUi = Required<ToolUiConfig>;

function resolveToolUi(explicit?: ToolUiConfig): ResolvedToolUi {
  let forceDebug = false;
  try {
    const sp = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    forceDebug = sp.has('botDebug');
  } catch {}
  if (forceDebug) return { frontendActions: 'debug', otherTools: 'debug' };
  return {
    frontendActions: explicit?.frontendActions ?? 'simple-replay',
    otherTools: explicit?.otherTools ?? 'simple',
  };
}

interface MessageListProps {
  messages: UIMessage[];
  userAvatar?: React.ReactNode | string;
  agentAvatar?: React.ReactNode | string;
  userMessageStyle?: React.CSSProperties;
  assistantMessageStyle?: React.CSSProperties;
  onReplayFrontendActions?: (toolCallId: string) => void;
  toolUi?: ToolUiConfig; // controls how tool messages render
}

export function MessageList({
  messages,
  userAvatar,
  agentAvatar,
  userMessageStyle,
  assistantMessageStyle,
  onReplayFrontendActions,
  toolUi,
}: MessageListProps) {
  const ui = resolveToolUi(toolUi);

  useMemo(() => {
    if (process.env.NODE_ENV !== 'production') {
      const anyToolResults = messages.filter(m => (m as any).type === 'tool_response_end');
      if (anyToolResults.length) {
        // eslint-disable-next-line no-console
        console.debug('[MessageList] tool_response_end events:', anyToolResults.map((m: any) => ({ tool: m.tool_name, id: m.id })));
        // eslint-disable-next-line no-console
        console.debug('[MessageList] toolUi.frontendActions:', ui.frontendActions);
      }
    }
  }, [messages, ui.frontendActions]);

  // Deduplicate by message id to avoid double rendering
  const deduped = useMemo<UIMessage[]>(() => {
    const out: UIMessage[] = [];
    const seen = new Set<string>();
    for (const m of messages) {
      const id = (m as any).id || '';
      if (id && seen.has(id)) continue;
      if (id) seen.add(id);
      out.push(m);
    }
    return out;
  }, [messages]);

  const renderMessage = (message: UIMessage) => {
    switch ((message as any).type) {
      case 'user':
      case 'assistant':
        return (
          <MessageBubble
            message={message}
            userAvatar={userAvatar}
            agentAvatar={agentAvatar}
            userMessageStyle={userMessageStyle}
            assistantMessageStyle={assistantMessageStyle}
          />
        );

      case 'tool_call_start': {
        const isFrontend = (message as any).tool_name === 'frontend_actions';
        if (isFrontend) {
          if (ui.frontendActions === 'debug') {
            return <ToolCallBubble message={message as any} agentAvatar={agentAvatar} />;
          }
          return null;
        }
        if (ui.otherTools === 'debug') {
          return <ToolCallBubble message={message as any} agentAvatar={agentAvatar} />;
        }
        return null;
      }

      case 'tool_response_end': {
        const isFrontend = (message as any).tool_name === 'frontend_actions';
        if (isFrontend && ui.frontendActions === 'hidden') return null;
        const allowReplay =
          isFrontend && (ui.frontendActions === 'simple-replay' || ui.frontendActions === 'debug');
        return (
          <ToolResultBubble
            message={message as any}
            agentAvatar={agentAvatar}
            onReplayFrontendActions={allowReplay ? onReplayFrontendActions : undefined}
          />
        );
      }

      case 'system_log':
      case 'page_context':
        return <SystemMessageBubble message={message as any} />;

      default:
        return null;
    }
  };

  return (
    <div className="space-y-2">
      {deduped.map((message, idx) => {
        const key = `${String((message as any).id ?? 'm')}-${idx}`;
        return (
          <motion.div
            key={key}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            layout
          >
            {renderMessage(message)}
          </motion.div>
        );
      })}
    </div>
  );
}
